import { useEffect } from "react";

/**
 * Signals to the boot splash in /public/index.html that real page content
 * has now painted. The HTML splash polls for `data-app-ready="1"` on
 * <html> and fades out as soon as it sees it — so callers don't have to
 * coordinate timing with the splash, they just call this once per top-level
 * page mount.
 *
 * Why a single attribute and not React state? Because the boot splash lives
 * outside React (it's HTML that exists before the JS bundle loads) and we
 * want zero coupling between the two trees. A custom DOM event is also
 * dispatched for the rare case where the splash already attached a listener.
 *
 * Idempotent — calling multiple times is safe.
 */
export default function useAppReady() {
  useEffect(() => {
    // Defer one rAF so the page has actually painted before we hide the
    // splash — avoids a one-frame flash of blank content.
    const id = window.requestAnimationFrame(() => {
      if (document.documentElement.getAttribute("data-app-ready") !== "1") {
        document.documentElement.setAttribute("data-app-ready", "1");
        try { window.dispatchEvent(new Event("kiddo-app-ready")); } catch { /* noop */ }
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, []);
}
